import { useServerClock, useServerTimeOffset } from '@exam-center/server-time';
import { Flex, Text, WatchLaterOutlinedIcon } from '@shared/components';
import { dayjs } from '@shared/utils';
import { memo } from 'react';

interface ServerClockProps {
  minified?: boolean;
}

/**
 * Displays the current server time in the sidebar.
 *
 * This is the **only** component that re-renders every second.
 * It uses `useServerClock` (local state + setInterval) so the
 * tick never propagates to parent or sibling components.
 */
function Clock(props: Readonly<ServerClockProps>) {
  const { minified: isMinified } = props;
  const { isLoaded } = useServerTimeOffset();
  const serverTime = useServerClock();

  const timeString = dayjs(serverTime).format('HH:mm');
  const seconds = dayjs(serverTime).second();

  return (
    <Flex
      component='li'
      sx={(theme) => ({
        alignItems: 'center',
        justifyContent: 'center',
        gap: isMinified ? 0 : theme.spacing(1),
        backgroundColor: theme.palette.grey[100],
        borderRadius: theme.shape.rounded(1.5),
        padding: isMinified ? theme.spacing(2.5, 1.5) : theme.spacing(2, 3),
        marginBottom: theme.spacing(1),
      })}
    >
      <WatchLaterOutlinedIcon
        sx={{
          fontSize: '24px',
          color: 'text.primary',
          width: isMinified ? 0 : '24px',
          opacity: isMinified ? 0 : 1,
          overflow: 'hidden',
          flexShrink: 0,
          transition: 'all 100ms ease-in-out',
        }}
      />
      <Text
        color='text.primary'
        variant={isMinified ? 'body2' : 'h5'}
        sx={{
          minWidth: '48px',
          fontWeight: 600,
          textAlign: 'center',
        }}
      >
        {isLoaded ? timeString : '--:--'}
        <Text component='span' sx={{ display: 'none' }}>
          {seconds}
        </Text>
      </Text>
    </Flex>
  );
}

export default memo(Clock);
